import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Dialog } from './ui/dialog.lib'
import { useT } from '../i18n'
import { orgContextI18n } from '../i18n/shared/org-context'
import { fetchOrgDirectory } from '../api/org-directory'
import { PexIcon } from './icons/PexIcon'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import type { DirectoryOrg } from '../mocks/data/organizations'
import type { OrgViewSide } from '../lib/orgViewState'

// The organization's UUID, shown and copyable. Operators need it for work that
// happens outside the UI — a rollout allowlist, a support ticket, a query — and
// it is otherwise reachable only by reading a network response by hand.
function OrgIdCopyButton({
  orgId,
  idLabel,
  label,
  doneLabel,
  failedLabel,
}: {
  orgId: string
  idLabel: string
  label: string
  doneLabel: string
  failedLabel: string
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const mountedRef = useRef(false)
  const feedbackTimerRef = useRef<number | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current)
    }
  }, [])

  async function copy() {
    let nextState: 'copied' | 'failed'
    try {
      await navigator.clipboard.writeText(orgId)
      nextState = 'copied'
    } catch {
      // Clipboard access is refused outside a secure context, and in some
      // embedded browsers. Say so rather than looking like it worked — the id
      // stays selectable next to the button.
      nextState = 'failed'
    }
    if (!mountedRef.current) return
    if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current)
    setState(nextState)
    feedbackTimerRef.current = window.setTimeout(() => {
      feedbackTimerRef.current = null
      if (mountedRef.current) setState('idle')
    }, 2000)
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-[var(--color-text-muted)]">
      <span className="shrink-0 text-xs">{idLabel}</span>
      <code className="max-w-[210px] truncate select-all font-mono text-[11px]" title={orgId}>{orgId}</code>
      <button
        type="button"
        onClick={copy}
        aria-label={label}
        title={state === 'failed' ? failedLabel : label}
        className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-[var(--radius-control)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
      >
        {state === 'copied' ? (
          <PexIcon name="success" size="sm" tone="success" />
        ) : (
          <PexIcon name="copy" size="sm" />
        )}
      </button>
      <span className="sr-only" role="status">
        {state === 'copied' ? doneLabel : state === 'failed' ? failedLabel : ''}
      </span>
    </div>
  )
}

export function OrgSearchModal({
  side,
  open,
  onClose,
  onSelect,
  actionLabel,
  description,
}: {
  side: OrgViewSide | null
  open: boolean
  onClose: () => void
  onSelect: (org: DirectoryOrg) => void
  /** Row action text; defaults to "Enter" for the org-context switcher. */
  actionLabel?: string
  /** Dialog description; defaults to the read-only "enter" explanation. */
  description?: string
}) {
  const t = useT(orgContextI18n)
  const [query, setQuery] = useState('')
  const searchId = useId()

  const dir = useQuery({
    queryKey: ['org-directory', side],
    queryFn: () => fetchOrgDirectory(side as OrgViewSide),
    enabled: side !== null,
  })
  const orgs = dir.data ?? []
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return orgs
    return orgs.filter(
      (o) =>
        o.orgName.toLowerCase().includes(q) ||
        (o.city ?? '').toLowerCase().includes(q) ||
        o.orgType.toLowerCase().includes(q),
    )
  }, [orgs, query])

  const dialogTitle =
    side === 'seller'
      ? t.search.selectOrgTitleSeller
      : side === 'supply'
        ? t.search.selectOrgTitleVendor
        : t.search.selectOrgTitleFallback

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setQuery('')
          onClose()
        }
      }}
    >
      <DialogContent
        mobilePresentation="fullscreen"
        className="flex max-h-[min(82dvh,760px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[800px] sm:p-0 max-sm:max-h-none"
      >
        <div className="shrink-0 border-b border-[var(--color-border)] px-5 pb-5 pt-6 sm:px-7 sm:pb-6 sm:pt-7">
          <DialogHeader className="pr-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-surface-muted)]">
                <PexIcon name="organization" size="sm" tone="signal" />
              </span>
              <DialogTitle className="text-xl">{dialogTitle}</DialogTitle>
              {!description && <Badge variant="neutral">{t.search.readOnlyEmphasis}</Badge>}
            </div>
            <DialogDescription className="max-w-[62ch] pt-1">{description ?? t.search.selectOrgDescription}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="shrink-0 px-5 pb-3 pt-5 sm:px-7">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor={searchId} className="text-sm font-semibold text-[var(--color-text)]">{t.search.searchLabel}</label>
            {!dir.isLoading && !dir.isError && <span aria-live="polite" className="text-xs tabular-nums text-[var(--color-text-muted)]">{t.search.resultCount(filtered.length)}</span>}
          </div>
          <div className="relative mt-2">
            <PexIcon name="search" size="sm" tone="muted" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              id={searchId}
              type="text"
              role="searchbox"
              inputMode="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search.searchPlaceholder}
              className="pl-10 pr-11"
            />
            {query && <button type="button" onClick={() => setQuery('')} aria-label={t.search.clearSearch} className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[var(--radius-control)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"><PexIcon name="close" size="sm" /></button>}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-7 sm:pb-7">
          {dir.isLoading ? (
            <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">{t.search.loading}</p>
          ) : dir.isError ? (
            <p className="py-12 text-center text-sm text-[var(--color-danger)]" role="alert">{t.search.loadError}</p>
          ) : filtered.length === 0 ? (
            <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">{t.search.noMatches}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)] overflow-hidden rounded-[var(--radius-panel)] border border-[var(--color-border)]">
              {filtered.map((o) => (
                <li key={o.orgId} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-4 py-4 hover:bg-[var(--color-surface-muted)] sm:items-center sm:px-5">
                  <div className="min-w-0">
                    <div className="break-words text-sm font-semibold text-[var(--color-text)]">{o.orgName}</div>
                    <OrgIdCopyButton orgId={o.orgId} idLabel={t.search.orgIdLabel} label={t.search.copyId} doneLabel={t.search.copyIdDone} failedLabel={t.search.copyIdFailed} />
                    <div className="mt-1 flex items-center gap-2 text-xs text-[var(--color-text-muted)]"><Badge variant="neutral">{o.orgType}</Badge><span>{t.search.memberCount(o.memberCount)}</span></div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start sm:self-auto"
                    onClick={() => { setQuery(''); onSelect(o) }}
                    aria-label={`${actionLabel ?? t.search.enterAction} ${o.orgName}`}
                  >{actionLabel ?? t.search.enterAction}</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
